import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, Trash2, Brain } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { aiApi } from "./aiApi";
import AIUsageRoleChart from "./AIUsageRoleChart";

const Stat = ({ label, value, tone = "text-slate-800", testid }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-100 px-2 py-1.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">{label}</div>
    <div className={`text-[15px] font-extrabold ${tone}`}>{value}</div>
  </div>
);

/** Papan pemantauan penggunaan AI (Superadmin). */
export function AIUsagePanel() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [price, setPrice] = useState(null);
  const load = () => aiApi.usage(days).then((x) => { setD(x); setPrice(x.harga); }).catch(() => toast.error("Gagal memuat statistik AI"));
  useEffect(() => { setD(null); load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const savePrice = async () => {
    try { await aiApi.savePricing({ input_per_m: Number(price.input_per_m), output_per_m: Number(price.output_per_m) }); toast.success("Harga token disimpan"); load(); }
    catch { toast.error("Gagal simpan harga"); }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-3" data-testid="ai-usage-panel">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="h-4 w-4 text-fuchsia-500" />
        <div className="font-extrabold text-sm text-slate-800 flex-1">Pemantauan Penggunaan AI</div>
        {[7, 30, 90].map((n) => (
          <button key={n} onClick={() => setDays(n)} data-testid={`ai-usage-days-${n}`}
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${days === n ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white text-slate-500 border-slate-200 hover:border-fuchsia-300"}`}>{n}h</button>
        ))}
      </div>
      {!d ? <div className="grid place-items-center h-24"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-1.5">
            <Stat label="Soalan" value={d.soalan} testid="ai-usage-questions" />
            <Stat label="Panggilan LLM" value={d.panggilan_llm} testid="ai-usage-calls" />
            <Stat label="Kadar ralat" value={`${d.kadar_ralat_pct}%`} tone={d.kadar_ralat_pct > 5 ? "text-rose-600" : "text-emerald-600"} testid="ai-usage-error-rate" />
            <Stat label="Token masuk" value={d.token_input.toLocaleString()} />
            <Stat label="Token keluar" value={d.token_output.toLocaleString()} />
            <Stat label="Anggaran kos" value={`$${d.anggaran_kos_usd}`} tone="text-fuchsia-700" testid="ai-usage-cost" />
          </div>
          <div className="text-[11px] text-slate-500">Purata masa jawapan: <b>{(d.purata_masa_ms / 1000).toFixed(1)}s</b> • Model: {d.model.join(", ") || "-"}</div>
          {d.harian.length > 0 && (
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.harian.map((x) => ({ ...x, t: x.tarikh.slice(5) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="t" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} width={28} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />
                  <Bar dataKey="panggilan" name="Panggilan" fill="#d946ef" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ralat" name="Ralat" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <AIUsageRoleChart harian={d.harian_peranan} ringkasan={d.ringkasan_peranan} peranan={d.peranan_senarai} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-1">Tool paling kerap</div>
              {d.tool_kerap.length === 0 && <div className="text-[11px] text-slate-400">Tiada lagi</div>}
              {d.tool_kerap.slice(0, 6).map((t) => <div key={t.tool} className="flex text-[11px] text-slate-600"><span className="flex-1 truncate">{t.tool}</span><b>{t.kali}</b></div>)}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-1">Soalan ikut peranan</div>
              {d.soalan_ikut_peranan.length === 0 && <div className="text-[11px] text-slate-400">Tiada lagi</div>}
              {d.soalan_ikut_peranan.map((r) => <div key={r.peranan} className="flex text-[11px] text-slate-600"><span className="flex-1">{r.peranan}</span><b>{r.soalan}</b></div>)}
            </div>
          </div>
          {price && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 flex-wrap">
              Harga USD / 1J token: masuk
              <Input type="number" step="0.01" value={price.input_per_m} onChange={(e) => setPrice({ ...price, input_per_m: e.target.value })} className="h-7 w-16 text-xs" data-testid="ai-usage-price-in" />
              keluar
              <Input type="number" step="0.01" value={price.output_per_m} onChange={(e) => setPrice({ ...price, output_per_m: e.target.value })} className="h-7 w-16 text-xs" data-testid="ai-usage-price-out" />
              <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={savePrice} data-testid="ai-usage-price-save">Simpan</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Senarai memori pilihan Superadmin yang diingati Mimi. */
export function AIMemoryPanel() {
  const [items, setItems] = useState(null);
  const load = () => aiApi.memory().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const del = async (id) => {
    if (!window.confirm("Padam memori ini?")) return;
    await aiApi.deleteMemory(id).catch(() => {}); toast.success("Memori dipadam"); load();
  };
  const shown = showAll ? items : items?.slice(0, 3);
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-3" data-testid="ai-memory-panel">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="h-4 w-4 text-violet-500" />
        <div className="font-extrabold text-sm text-slate-800 flex-1">Memori Pilihan Mimi</div>
        {items?.length > 0 && <span className="text-[10.5px] font-bold text-violet-600 bg-violet-50 rounded-full px-2 py-0.5" data-testid="ai-memory-count">{items.length}</span>}
      </div>
      <div className="text-[11px] text-slate-400 mb-2">Cth di chat: "Ingat, saya suka laporan dalam bentuk jadual".</div>
      {items === null && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      {items?.length === 0 && <div className="text-xs text-slate-400">Belum ada memori.</div>}
      <div className="space-y-1.5">
        {shown?.map((m, i) => {
          const open = expanded === m.id;
          return (
            <div key={m.id} className="flex items-start gap-2 rounded-xl bg-slate-50 border border-slate-100 px-2.5 py-2" data-testid="ai-memory-item">
              <span className="mt-0.5 grid place-items-center h-4 w-4 rounded-full bg-violet-100 text-violet-600 text-[9px] font-extrabold shrink-0">{i + 1}</span>
              <button type="button" onClick={() => setExpanded(open ? null : m.id)} className="flex-1 min-w-0 text-left" data-testid="ai-memory-toggle">
                <div className={`text-[12px] leading-snug text-slate-700 whitespace-pre-line ${open ? "" : "line-clamp-2"}`}>{m.text}</div>
                {(m.text || "").length > 90 && <div className="text-[10.5px] font-bold text-violet-500 mt-0.5">{open ? "Tutup" : "Baca penuh"}</div>}
              </button>
              <button onClick={() => del(m.id)} className="text-slate-300 hover:text-rose-500 mt-0.5" aria-label="Padam memori" data-testid="ai-memory-delete"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>
      {items?.length > 3 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 w-full text-[11.5px] font-bold text-violet-600 hover:text-violet-700 py-1" data-testid="ai-memory-show-all">
          {showAll ? "Tunjuk ringkas" : `Lihat semua (${items.length})`}
        </button>
      )}
    </div>
  );
}
